import { shouldEnableFormTracking } from '../../../config/SiteConfigManager';
import { logger } from '../../../utils/core/logger';
import type {
    FormDetectionContext,
    FormDetectionManagerInterface,
    FormDetectionStrategy,
    FormElement,
    FormTrackingData,
} from '../types';

// Strategy 2: HubSpot Form Detection (React-based forms that don't fire native submit events)
// HubSpot's newer forms (hsfc-*) handle submission entirely in React, replacing the form
// with a success/error message. We detect submission by watching for this DOM replacement.
export class HubSpotFormStrategy implements FormDetectionStrategy {
    name = 'hubspot_form';
    private manager: FormDetectionManagerInterface | null = null;
    private observers: MutationObserver[] = [];

    setManager(manager: FormDetectionManagerInterface): void {
        this.manager = manager;
    }

    canHandle(form: FormElement, context: FormDetectionContext): boolean {
        const isHubSpotForm =
            form.classList.contains('hsfc-Form') ||
            form.classList.contains('hs-form') ||
            form.hasAttribute('data-hsfc-id') ||
            (form.getAttribute('data-form-id') !== null && form.action?.includes('hsforms.com'));

        if (!isHubSpotForm) return false;

        const shouldTrack = shouldEnableFormTracking(context.domain);

        logger.debug('🟠 HubSpotFormStrategy.canHandle', {
            formId: form.id,
            isHubSpotForm,
            shouldTrack,
        });

        return shouldTrack;
    }

    apply(form: FormElement, context: FormDetectionContext): void {
        const fieldCount = form.querySelectorAll('input, select, textarea').length;
        const formWrapper =
            form.closest('.hsfc-FormWrapper, .hbspt-form, [id^="hbspt-form"]') ||
            form.parentElement;

        if (!formWrapper) {
            logger.warn('🟠 HubSpot form: no parent wrapper found, skipping');
            return;
        }

        const baseData = {
            formId: form.getAttribute('data-form-id') || form.id || 'hubspot_form',
            formClass: form.className || '',
            pageUrl: context.currentUrl,
            formAction: form.action || '',
            formMethod: form.method || 'POST',
            fieldCount,
            detectionMethod: this.name,
            provider: 'hubspot',
        };

        logger.debug('🟠 HubSpot form: setting up MutationObserver on wrapper', {
            formId: baseData.formId,
            wrapperTag: formWrapper.tagName,
            wrapperClass: (formWrapper as HTMLElement).className,
        });

        // Watch for DOM replacement: HubSpot removes the form and inserts a success/error message
        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                // Check if the form was removed from DOM
                for (let i = 0; i < mutation.removedNodes.length; i++) {
                    const removed = mutation.removedNodes[i];
                    if (
                        removed === form ||
                        (removed instanceof Element && removed.contains(form))
                    ) {
                        this.handleFormRemoval(form, formWrapper as HTMLElement, baseData, context);
                        observer.disconnect();
                        return;
                    }
                }

                // Check if a success message was added (HubSpot uses .submitted-message or similar)
                for (let i = 0; i < mutation.addedNodes.length; i++) {
                    const added = mutation.addedNodes[i];
                    if (added instanceof Element) {
                        const isSuccessMessage =
                            added.classList.contains('submitted-message') ||
                            added.querySelector('.submitted-message') !== null ||
                            added.getAttribute('role') === 'status' ||
                            added.getAttribute('data-hsfc-id') === 'SubmittedMessage';

                        if (isSuccessMessage) {
                            logger.debug(
                                '🟠 HubSpot form: success message detected via added node'
                            );

                            if (this.manager?.shouldTrackSubmission(form)) {
                                context.onFormSubmit({
                                    ...baseData,
                                    status: 'success',
                                    indicators: ['hubspot_success_message_added'],
                                });
                            }
                            observer.disconnect();
                            return;
                        }
                    }
                }
            }
        });

        observer.observe(formWrapper, { childList: true, subtree: true });
        this.observers.push(observer);

        // Also listen for submit button click as a fallback signal
        const submitButton = form.querySelector('button[type="submit"], input[type="submit"]');
        if (submitButton) {
            submitButton.addEventListener('click', () => {
                logger.debug('🟠 HubSpot form: submit button clicked', {
                    formId: baseData.formId,
                });

                // Wait for HubSpot to process, then check if form is still visible
                setTimeout(() => {
                    if (!document.body.contains(form)) {
                        // Form removed but observer might have missed it (race condition safety net)
                        if (this.manager?.shouldTrackSubmission(form)) {
                            context.onFormSubmit({
                                ...baseData,
                                status: 'success',
                                indicators: ['hubspot_form_removed_after_click'],
                            });
                        }
                    }
                }, 3000);
            });
        }
    }

    private handleFormRemoval(
        form: FormElement,
        wrapper: HTMLElement,
        baseData: Omit<FormTrackingData, 'status'>,
        context: FormDetectionContext
    ): void {
        // Small delay to let HubSpot render the replacement content
        setTimeout(() => {
            const wrapperText = wrapper.textContent?.toLowerCase() || '';
            const hasErrorIndicators =
                wrapperText.includes('error') ||
                wrapperText.includes('erreur') ||
                wrapper.querySelector('[role="alert"], .hs-error-msgs, .hs-error-msg') !== null;

            const status = hasErrorIndicators ? 'failed' : 'success';
            const indicators: string[] = ['hubspot_form_replaced'];

            if (hasErrorIndicators) {
                indicators.push('hubspot_error_in_replacement');
            } else {
                if (
                    wrapperText.includes('thank') ||
                    wrapperText.includes('merci') ||
                    wrapperText.includes('success')
                ) {
                    indicators.push('hubspot_success_text_detected');
                }
            }

            logger.debug('🟠 HubSpot form: form removed, tracking submission', {
                formId: baseData.formId,
                status,
                indicators,
                replacementText: wrapperText.substring(0, 100),
            });

            if (this.manager?.shouldTrackSubmission(form)) {
                context.onFormSubmit({ ...baseData, status, indicators });
            }
        }, 500);
    }

    cleanup(): void {
        this.observers.forEach((obs) => {
            obs.disconnect();
        });
        this.observers = [];
    }
}
